import type { AdministratieRow } from '@/api/administratie';
import { ToggleCell } from './ToggleCell';
import { LimosaBadge, verplichtBronLabel } from './LimosaBadge';
import type { WerfGroup } from './groupByWerf';
import { projectleiderColorByName } from '@/utils/planning';
import { CheckinWorkField } from './CheckinWorkField';
import { WorkerTableRows, WorkerCardList, companySummary, DocumentenKnop, type WorkerActions } from './WorkerRows';

export interface RowActions extends WorkerActions {
  busyKeys: Set<string>;
  onGemeld: (row: AdministratieRow, value: boolean) => void;
  onIngecheckt: (row: AdministratieRow, value: boolean) => void;
  /** Absent: the Checkin@work number is read-only. */
  onCheckinWork?: (projectId: number, value: string) => void;
  /** Absent: no documents download. */
  documenten?: {
    /** The werf whose zip is being made, by group key. */
    busyKey: string | null;
    /** Anyone present on this werf, judged on the whole day (not the filtered rows). */
    hasPeople: (group: WerfGroup) => boolean;
    onDownload: (group: WerfGroup) => void;
  };
}

const gemeldKey = (r: AdministratieRow) => `gemeld-${r.projectId}-${r.installerId}`;
const ingechecktKey = (r: AdministratieRow) => `ingecheckt-${r.key}`;

function toggleCells(row: AdministratieRow, actions: RowActions) {
  if (row.installerId == null) {
    return { gemeld: <span className="text-td-light">—</span>, ingecheckt: <span className="text-td-light">—</span> };
  }
  return {
    gemeld: (
      <ToggleCell
        value={row.gemeld}
        label={`Meldingsmail ${row.installerName ?? ''}`}
        busy={actions.busyKeys.has(gemeldKey(row))}
        onToggle={(v) => actions.onGemeld(row, v)}
      />
    ),
    ingecheckt: (
      <ToggleCell
        value={row.ingecheckt}
        label={`Ingecheckt ${row.installerName ?? ''}`}
        busy={actions.busyKeys.has(ingechecktKey(row))}
        onToggle={(v) => actions.onIngecheckt(row, v)}
      />
    ),
  };
}

/** A one-person installer is the person: its documents open from its own row. */
function ownDocumenten(row: AdministratieRow, actions: RowActions) {
  if (row.installerId == null || row.workers) return null;
  return <DocumentenKnop person={{ id: row.installerId, name: row.installerName ?? 'Installateur' }} actions={actions} />;
}

const installerLabel = (row: AdministratieRow) =>
  row.installerName ?? <span className="italic font-normal text-td-light">Geen installateur</span>;

/**
 * The werf header's tint and left border: the planning's colour for the
 * projectleider (one source, `projectleiderColorByName`), neutral grey without one.
 * A light tint with dark text, never a dark fill.
 */
export function werfHeaderColor(projectleider: string | null): string {
  if (!projectleider) return 'bg-gray-50 border-l-gray-300';
  const c = projectleiderColorByName(projectleider);
  return `${c.bg} ${c.border}`;
}

/**
 * Project, klant, werf address and Checkin@work: shown once per werf, as the
 * strong line the installer rows hang under. The Checkin@work number is always
 * spelled out (never truncated) and says "geen" when the order has none.
 */
function WerfHeading({ group, actions }: { group: WerfGroup; actions: RowActions }) {
  const { onCheckinWork } = actions;
  const projectId = group.projectId;
  return (
    <div className="min-w-0">
      <h3 className="text-base font-extrabold text-td-navy leading-tight break-words">{group.projectName}</h3>
      <p className="mt-0.5 text-sm font-semibold text-td-navy break-words">{group.klant ?? 'Geen klant'}</p>
      <p className="text-sm text-gray-600 break-words">{group.werfAdres ?? 'Geen werfadres'}</p>
      <p className="text-sm text-gray-700">
        <span className="font-semibold text-td-navy">Projectleider: </span>
        {group.projectleider ?? <span className="text-td-muted">geen</span>}
      </p>
      <CheckinWorkField
        value={group.checkinWorkNr}
        editable={onCheckinWork != null && projectId != null}
        hasSaleOrder={group.hasSaleOrder}
        busy={actions.busyKeys.has(`checkinwork-${projectId}`)}
        onSave={(v) => { if (projectId != null) onCheckinWork?.(projectId, v); }}
      />
      {actions.documenten && projectId != null && <DocumentenAction group={group} documenten={actions.documenten} />}
    </div>
  );
}

/** The werf's documents as one zip; says so instead when nobody is marked present. */
function DocumentenAction({ group, documenten }: { group: WerfGroup; documenten: NonNullable<RowActions['documenten']> }) {
  if (!documenten.hasPeople(group)) {
    return <p className="mt-1 text-xs text-td-muted">Documenten: Nog niemand aangeduid als aanwezig</p>;
  }
  const busy = documenten.busyKey === group.key;
  return (
    <button
      type="button"
      onClick={() => documenten.onDownload(group)}
      disabled={busy}
      className="mt-2 inline-flex items-center gap-1.5 min-h-[36px] lg:min-h-0 px-3 py-1 text-xs font-semibold text-td-blue bg-white border border-td-blue/40 rounded-full hover:bg-td-blue/10 disabled:opacity-60 transition"
    >
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" /></svg>
      {busy ? 'Documenten ophalen...' : 'Documenten'}
    </button>
  );
}

const HEADERS = ['Taak', 'Installateur', 'Aanwezig', 'Meldingsmail', 'Ingecheckt', 'Limosa'];

export function AdministratieTable({ groups, actions }: { groups: WerfGroup[]; actions: RowActions }) {
  return (
    <div className="overflow-x-auto bg-white rounded-xl border border-gray-200">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wider text-td-muted">
          <tr>
            {HEADERS.map((h) => <th key={h} className="px-3 py-2 whitespace-nowrap">{h}</th>)}
          </tr>
        </thead>
        {groups.map((group) => (
          <tbody key={group.key} className="border-t-2 border-gray-200" data-testid="werf-group">
            <tr className={`border-l-4 ${werfHeaderColor(group.projectleider)}`} data-testid="werf-header">
              <th scope="rowgroup" colSpan={HEADERS.length} className="px-3 py-3 text-left font-normal">
                <WerfHeading group={group} actions={actions} />
              </th>
            </tr>
            {group.rows.map((row) => {
              const t = toggleCells(row, actions);
              const company = row.workers ? companySummary(row) : null;
              return [
                <tr key={row.key} className="align-middle border-t border-gray-100 bg-white text-gray-700" data-testid="installer-row">
                  <td className="px-3 py-2 pl-10 text-xs text-td-blue">{row.taskName}</td>
                  <td className="px-3 py-2">{installerLabel(row)}</td>
                  <td className="px-3 py-2">{company ? company.aanwezig : <span className="text-td-light">—</span>}</td>
                  <td className="px-3 py-2">{t.gemeld}</td>
                  <td className="px-3 py-2">{company ? company.ingecheckt : t.ingecheckt}</td>
                  <td className="px-3 py-2">
                    <LimosaBadge row={row} onOpen={actions.onLimosa} />
                    {row.limosaVerplichtBron && <p className="mt-0.5 text-[11px] text-td-muted whitespace-nowrap">{verplichtBronLabel(row.limosaVerplichtBron)}</p>}
                    {ownDocumenten(row, actions)}
                  </td>
                </tr>,
                <WorkerTableRows key={`${row.key}-workers`} row={row} actions={actions} />,
              ];
            })}
          </tbody>
        ))}
      </table>
    </div>
  );
}

export function AdministratieCards({ groups, actions }: { groups: WerfGroup[]; actions: RowActions }) {
  return (
    <div className="space-y-3 pb-20">
      {groups.map((group) => (
        <div key={group.key} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-testid="administratie-card">
          <div className={`border-l-4 p-4 ${werfHeaderColor(group.projectleider)}`} data-testid="werf-header">
            <WerfHeading group={group} actions={actions} />
          </div>
          <ul className="divide-y divide-gray-100 pl-6 pr-4">
            {group.rows.map((row) => {
              const t = toggleCells(row, actions);
              const company = row.workers ? companySummary(row) : null;
              return (
                <li key={row.key} className="py-2" data-testid="administratie-card-row">
                  <p className="text-sm font-medium text-gray-700">{installerLabel(row)}</p>
                  <p className="text-xs text-td-blue">{row.taskName}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-td-muted">
                    <span className="flex items-center gap-2">Meldingsmail {t.gemeld}</span>
                    {company && <span className="flex items-center gap-2">Aanwezig {company.aanwezig}</span>}
                    <span className="flex items-center gap-2">Ingecheckt {company ? company.ingecheckt : t.ingecheckt}</span>
                    <span className="flex items-center gap-2">Limosa <LimosaBadge row={row} onOpen={actions.onLimosa} /></span>
                    {row.limosaVerplichtBron && <span className="text-[11px] text-td-muted">{verplichtBronLabel(row.limosaVerplichtBron)}</span>}
                    {ownDocumenten(row, actions)}
                  </div>
                  <WorkerCardList row={row} actions={actions} />
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}
